/**
 * アセット関連のユーティリティ関数
 */

/**
 * アセットURLを生成する
 * @param {string} imagePath - 画像パス (例: "/shops/3/card_images/abc.jpg")
 * @returns {string} 完全なアセットURL
 */
export const getAssetUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // すでに完全なURLの場合はそのまま返す
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 先頭のスラッシュを除去
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  
  return `${process.env.REACT_APP_ASSET_URL}/${cleanPath}`;
};

/**
 * 新しいAssetController経由でアップロードURLを生成する
 * @param {object} params - アップロードパラメータ
 * @param {string} params.fileType - ファイルタイプ ('image', 'document', 'effect_pattern', 'media', 'archive', 'font', 'web')
 * @param {string} params.extension - ファイル拡張子
 * @param {number} params.shopId - ショップID
 * @param {string} params.token - 認証トークン
 * @returns {Promise<object>} アップロード情報
 */
export const generateUploadUrl = async ({ fileType, extension, shopId, token }) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      file_type: fileType,
      extension: extension,
      shop_id: shopId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Upload URL generation failed: ${response.statusText}`);
  }

  return response.json();
};

/**
 * S3にファイルを直接アップロード
 * @param {string} uploadUrl - プリサインドアップロードURL
 * @param {File} file - アップロードするファイル
 * @returns {Promise<Response>}
 */
export const uploadToS3 = async (uploadUrl, file) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`File upload failed: ${response.statusText}`);
  }

  return response;
};

/**
 * ファイル拡張子を取得
 * @param {string} filename - ファイル名
 * @returns {string} 拡張子
 */
export const getFileExtension = (filename) => {
  return filename.split('.').pop().toLowerCase();
};

/**
 * ファイルタイプを判定
 * @param {string} filename - ファイル名
 * @returns {string} ファイルタイプ
 */
export const getFileType = (filename) => {
  const extension = getFileExtension(filename);
  
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'ico', 'bmp', 'tiff', 'tif', 'avif'];
  const documentExtensions = ['pdf', 'txt', 'md', 'rtf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
  const mediaExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'mp4', 'avi', 'mov', 'wmv', 'webm'];
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz'];
  const fontExtensions = ['woff', 'woff2', 'ttf', 'eot', 'otf'];
  const webExtensions = ['css', 'js', 'json', 'xml', 'html', 'htm'];

  if (imageExtensions.includes(extension)) return 'image';
  if (documentExtensions.includes(extension)) return 'document';
  if (mediaExtensions.includes(extension)) return 'media';
  if (archiveExtensions.includes(extension)) return 'archive';
  if (fontExtensions.includes(extension)) return 'font';
  if (webExtensions.includes(extension)) return 'web';
  
  return 'document'; // デフォルト
};